import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Home from "./components/Home";
import "./App.css";
import * as amplitude from "@amplitude/analytics-browser";
import { HelmetProvider } from "react-helmet-async";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import ContactUs from "./components/ContactUs";
import SalicylicAcidTreatment from "./components/SalicylicAcidTreatment";
import ForumIcon from "@mui/icons-material/Forum";
import VideocamIcon from "@mui/icons-material/Videocam";
import { ThemeProvider, CssBaseline } from "@mui/material";
import theme from "./theme";
import {
  Typography,
  Button,
  Stack,
  Container,
  Grid,
  Divider,
} from "@mui/material";

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AppContent />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

function AppContent() {
  const [isLoaded, setIsLoaded] = useState(false);
  const location = useLocation();

  // Initialize Amplitude Analytics
  useEffect(() => {
    amplitude.init("cf7c61c840f5ad70711936c8a8e115fb");
    setIsLoaded(true);

    // Initialize page view tracking
    const trackPageView = () => {
      amplitude.logEvent("Page View", {
        page: window.location.pathname,
      });
    };

    // Track the initial page view
    trackPageView();

    // Setup a listener for route changes to track page views
    const unlisten = () => {
      // This is a mock unlisten function since we're not using a real router listener
      // but are reacting to location changes through useEffect
    };

    return () => {
      if (typeof unlisten === "function") {
        unlisten();
      }
    };
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (isLoaded) {
      amplitude.logEvent("Page View", {
        page: location.pathname,
      });
    }
  }, [location.pathname, isLoaded]);

  // Function to scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        }}
      >
        <Toolbar
          sx={{ justifyContent: "space-between", py: { xs: 0.5, sm: 1 } }}
        >
          <Link
            to="/"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src="/logo512.png"
              alt="The Wart Guy Logo"
              sx={{ height: { xs: 40, sm: 50 } }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                ml: 1,
                display: { xs: "none", sm: "block" },
              }}
            >
              TheWartGuy
            </Typography>
          </Link>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              size="small"
              component="a"
              href="https://discord.gg/Fq69xjmDVx"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<ForumIcon />}
              aria-label="Join Discord for help"
              onClick={() => {
                amplitude.logEvent("Join Discord", {
                  location: "header",
                });
              }}
              sx={{
                display: { xs: "none", sm: "flex" },
              }}
            >
              Get Help
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="small"
              component="a"
              href="https://www.tiktok.com/@thewartguy?is_from_webapp=1&sender_device=pc"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<VideocamIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
              aria-label="View TikTok content"
              onClick={() => {
                amplitude.logEvent("View TikTok", {
                  location: "header",
                });
              }}
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 0.75 },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: "inline", sm: "inline" } }}
              >
                TikTok
              </Box>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: "1 0 auto",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          position: "relative",
          visibility: !isLoaded ? "hidden" : "visible",
          opacity: !isLoaded ? 0 : 1,
          transition: "opacity 0.2s ease-in",
        }}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route
            path="/salicylic-acid-treatment"
            element={<SalicylicAcidTreatment />}
          />
        </Routes>
      </Box>

      <Box
        component="footer"
        sx={{
          padding: { xs: "30px 0", md: "40px 0" },
          borderTop: "1px solid #e7e7e7",
          backgroundColor: "background.paper",
          width: "100%",
          flexShrink: 0,
          position: "relative",
          bottom: 0,
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, md: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, mb: { xs: 1, md: 2 } }}
              >
                The Wart Guy
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: { xs: 1.5, md: 2 },
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                }}
              >
                A personal journey to eliminate stubborn plantar warts with a
                proven 60-day protocol.
              </Typography>
              <Stack
                direction="row"
                spacing={{ xs: 1, sm: 2 }}
                sx={{ "& .MuiButton-root": { minWidth: { xs: 0, sm: 64 } } }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href="https://www.tiktok.com/@thewartguy?is_from_webapp=1&sender_device=pc"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={
                    <VideocamIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  }
                  aria-label="View TikTok videos"
                  onClick={() => {
                    amplitude.logEvent("View TikTok", {
                      location: "footer",
                    });
                  }}
                  sx={{
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.75 },
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "inline", sm: "inline" } }}
                  >
                    TikTok
                  </Box>
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href="https://discord.gg/Fq69xjmDVx"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={
                    <ForumIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                  }
                  aria-label="Join Discord community"
                  onClick={() => {
                    amplitude.logEvent("Join Discord", {
                      location: "footer",
                    });
                  }}
                  sx={{
                    px: { xs: 1, sm: 1.5 },
                    py: { xs: 0.5, sm: 0.75 },
                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: "inline", sm: "inline" } }}
                  >
                    Discord
                  </Box>
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={6} sm={3} md={4}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 1, md: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Quick Links
              </Typography>
              <Stack spacing={{ xs: 0.5, md: 1 }}>
                <Link to="/" style={{ textDecoration: "none" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      "&:hover": { color: "primary.main" },
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      py: 0.25,
                    }}
                  >
                    Home
                  </Typography>
                </Link>
                <Link
                  to="/salicylic-acid-treatment"
                  style={{ textDecoration: "none" }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      "&:hover": { color: "primary.main" },
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      py: 0.25,
                    }}
                  >
                    Treatment Protocol
                  </Typography>
                </Link>
                <a
                  href="https://linktr.ee/thewartguy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                  onClick={() => {
                    amplitude.logEvent("View Linktree", {
                      location: "footer",
                    });
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      "&:hover": { color: "primary.main" },
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      py: 0.25,
                    }}
                  >
                    All Products
                  </Typography>
                </a>
              </Stack>
            </Grid>

            <Grid item xs={6} sm={3} md={4}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: { xs: 1, md: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Legal
              </Typography>
              <Stack spacing={{ xs: 0.5, md: 1 }}>
                <Link to="/privacy" style={{ textDecoration: "none" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      "&:hover": { color: "primary.main" },
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      py: 0.25,
                    }}
                  >
                    Privacy Policy
                  </Typography>
                </Link>
                <Link to="/terms" style={{ textDecoration: "none" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      "&:hover": { color: "primary.main" },
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      py: 0.25,
                    }}
                  >
                    Terms of Service
                  </Typography>
                </Link>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      "&:hover": { color: "primary.main" },
                      fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                      py: 0.25,
                    }}
                  >
                    Contact Us
                  </Typography>
                </Link>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: { xs: 2.5, md: 4 } }} />

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            © {new Date().getFullYear()} The Wart Guy. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
